import { useState } from 'react';
import { useGridApiRef, type GridRowId } from '@mui/x-data-grid';

/**
 * Commits cells that are still in edit mode before a Settings page builds its
 * proposed value.
 *
 * Without this, clicking Preview while a cell (e.g. Expires) is being edited read the
 * rows state before the grid's own commit landed, and the edit was silently lost.
 * `withPendingEdits(rows)` merges the in-progress cell values into the rows
 * synchronously and exits edit mode; `editing` lets Preview stay enabled while a cell
 * is open (an uncommitted edit is not yet "dirty").
 */
export function usePendingGridEdits<Row extends { id: GridRowId }>() {
  const apiRef = useGridApiRef();
  const [editing, setEditing] = useState(false);

  function withPendingEdits(rows: Row[]): Row[] {
    const editRows = apiRef.current?.state?.editRows ?? {};
    const openRowIds = Object.keys(editRows);
    if (openRowIds.length === 0) return rows;

    const merged = rows.map((row) => {
      const cells = editRows[row.id];
      if (!cells) return row;
      const patch = Object.fromEntries(
        Object.entries(cells).map(([field, cell]) => [field, (cell as { value: unknown }).value])
      );
      return { ...row, ...patch } as Row;
    });

    // Values are applied above; close the editors without a second (async) commit.
    for (const id of openRowIds) {
      for (const field of Object.keys(editRows[id])) {
        const rowId = merged.find((row) => String(row.id) === id)?.id ?? id;
        if (apiRef.current?.getCellMode(rowId, field) === 'edit') {
          apiRef.current.stopCellEditMode({ id: rowId, field, ignoreModifications: true });
        }
      }
    }
    setEditing(false);
    return merged;
  }

  return {
    editing,
    withPendingEdits,
    gridProps: {
      apiRef,
      onCellEditStart: () => setEditing(true),
      onCellEditStop: () => setEditing(false),
    },
  };
}
